/**
 * The pure parts of the failure standard's reference code (E3), kept apart
 * from the Sentry SDK, i18n and Axios so a plain test can run them.
 *
 * A failure on our side is sent to Sentry with an event id Grow picks itself
 * (32 hex characters), tagged "reference" with its first 8. The gardener's
 * message ends with "Reference: d53e8822", so a screenshot leads to the event
 * by searching `reference:d53e8822` in Sentry (a bare event-id prefix is not
 * searchable there; the tag is). With Sentry off there is no event, so no
 * code and no line. A server failure that carries its own `ref` in the error
 * body (the server's event) shows that one instead.
 *
 * The reference line rides inside the message string after a marker
 * (REFERENCE_MARK), so every place that already passes the standard sentence
 * around as text (toasts, errorMessage, screens) keeps working; FailureText
 * splits it back out and draws it small.
 *
 * @module serverFailureCore
 */

/**
 * How many characters of the event id the gardener sees. An engineering value.
 *
 * @kept K1
 */
const REFERENCE_LENGTH = 8;

/**
 * Marks where the reference line starts inside a message string (an invisible
 * separator nobody types).
 *
 * @kept K1
 */
export const REFERENCE_MARK = '⁣';

/** The parts of a Sentry scope the capture uses. */
export interface ScopeLike {
  /** Tags the event. */
  setTag: (key: string, value: string) => void;
  /** Adds extra data to the event. */
  setExtra: (key: string, value: unknown) => void;
  /** Groups events into one issue. */
  setFingerprint: (parts: string[]) => void;
}

/** The parts of the Sentry SDK the capture uses, so a test can stand in for it. */
export interface SentryLike {
  /** The running client, or undefined when Sentry is off. */
  getClient: () => unknown;
  /** Runs the callback with a scope that tags the event. */
  withScope: (fn: (scope: ScopeLike) => void) => void;
  /** Sends the error; returns the event id. */
  captureException: (error: unknown, hint?: { event_id?: string }) => string;
}

/** What a capture is tagged with. */
export interface CaptureContext {
  /** Tags for the event. */
  tags?: Record<string, string>;
  /** Extras for the event. */
  extras?: Record<string, unknown>;
  /** Groups events into one issue. */
  fingerprint?: string[];
}

/**
 * The first characters of an event id: the code a gardener quotes.
 *
 * @param eventId - A Sentry event id.
 * @returns The code, or undefined when there is no id.
 */
export function referenceCode(eventId: string | undefined | null): string | undefined {
  const id = typeof eventId === 'string' ? eventId.replace(/-/g, '').trim() : '';
  return id ? id.slice(0, REFERENCE_LENGTH) : undefined;
}

/**
 * A fresh event id: 32 random hex characters.
 *
 * @returns The id.
 */
function newEventId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/**
 * Sends an error to Sentry under an id of Grow's own choosing, tagged
 * "reference" with its first 8 characters, and hands the code back. Without a
 * running Sentry client nothing is sent and there is no code.
 *
 * @param sdk - Sentry (or a stand-in).
 * @param error - What to report.
 * @param context - Tags, extras and fingerprint for the event.
 * @returns The reference code, or undefined when Sentry is off.
 */
export function captureWithReference(sdk: SentryLike, error: unknown, context: CaptureContext): string | undefined {
  if (!sdk.getClient()) return undefined;
  const eventId = newEventId();
  const code = referenceCode(eventId);
  let sentId: string | undefined;
  sdk.withScope((scope) => {
    for (const [key, value] of Object.entries(context.tags ?? {})) scope.setTag(key, value);
    for (const [key, value] of Object.entries(context.extras ?? {})) scope.setExtra(key, value);
    if (context.fingerprint) scope.setFingerprint(context.fingerprint);
    if (code) scope.setTag('reference', code);
    sentId = sdk.captureException(error, { event_id: eventId });
  });
  return code && sentId ? code : undefined;
}

/**
 * Tags an outgoing event with "reference" (the first 8 characters of its id)
 * when it has none, so every event, including the crash screen's, is found the
 * same way. Used as Sentry's beforeSend.
 *
 * @param event - The event about to be sent.
 * @returns The same event, tagged.
 */
export function withReferenceTag<E extends { event_id?: string; tags?: Record<string, unknown> }>(event: E): E {
  const code = referenceCode(event.event_id);
  if (code && !event.tags?.reference) event.tags = { ...event.tags, reference: code };
  return event;
}

/**
 * The server's own reference from an error body (`ref`), when it is a short
 * hex code.
 *
 * @param body - A response body.
 * @returns The code, or undefined.
 */
export function referenceFromBody(body: unknown): string | undefined {
  const ref = (body as { ref?: unknown } | null | undefined)?.ref;
  return typeof ref === 'string' && /^[0-9a-f]{8}$/i.test(ref) ? ref.toLowerCase() : undefined;
}

/**
 * Puts the reference line after a sentence.
 *
 * @param sentence - The message.
 * @param referenceLine - The words with the code ("Reference: d53e8822"), or undefined.
 * @returns The sentence alone, or with the line after the marker.
 */
export function joinReference(sentence: string, referenceLine: string | undefined): string {
  return referenceLine ? `${sentence}${REFERENCE_MARK}${referenceLine}` : sentence;
}

/**
 * Splits a message back into its sentence and its reference line.
 *
 * @param text - A message, possibly made by joinReference.
 * @returns The sentence and the reference line (null when there is none).
 */
export function splitReference(text: string): { sentence: string; reference: string | null } {
  const at = text.indexOf(REFERENCE_MARK);
  if (at < 0) return { sentence: text, reference: null };
  return { sentence: text.slice(0, at), reference: text.slice(at + REFERENCE_MARK.length) };
}
